// src/helpers/mrt/resolveResponsiveToolbarActions.js

/**
 * Resolve quais ações da toolbar serão exibidas no Desktop e no Mobile.
 *
 * ------------------------------------------------------------------------
 * DESKTOP
 * ------------------------------------------------------------------------
 * Todas as ações são exibidas normalmente.
 *
 * Exemplo:
 *
 * Novo | Imprimir | Baixar | Relatórios | ...
 *
 *
 * ------------------------------------------------------------------------
 * MOBILE
 * ------------------------------------------------------------------------
 * Apenas as ações marcadas com:
 *
 *      mobileVisible: true
 *
 * permanecem visíveis.
 *
 * As demais serão exibidas no menu de opções (⋮).
 *
 * Exemplo:
 *
 * [
 *   {
 *      key: 'novo',
 *      mobileVisible: true
 *   },
 *   {
 *      key: 'imprimir',
 *      mobileVisible: true
 *   },
 *   {
 *      key: 'baixar'
 *   }
 * ]
 *
 * Resultado:
 *
 * Novo | Imprimir | (⋮)
 *                     Baixar
 *
 *
 * ------------------------------------------------------------------------
 * COMPATIBILIDADE
 * ------------------------------------------------------------------------
 *
 * As telas antigas não possuem a propriedade mobileVisible.
 *
 * Para não quebrar nenhuma tela, caso nenhuma ação esteja marcada,
 * automaticamente as primeiras ações permanecem visíveis.
 *
 * Assim a migração poderá ser feita aos poucos.
 */

export default function resolveResponsiveToolbarActions(
    actions = [],
    {
        /**
         * Indica se a tela atual está sendo exibida em um dispositivo móvel.
         */
        isMobile = false,

        /**
         * Quantidade de botões visíveis quando nenhuma ação possuir
         * mobileVisible=true.
         *
         * Utilizado apenas para manter compatibilidade com telas antigas.
         */
        defaultMobileVisibleCount = 1,

        /**
         * Segurança.
         *
         * Mesmo que várias ações sejam marcadas como mobileVisible,
         * limita a quantidade de botões exibidos no celular.
         */
        maxMobileVisibleActions = 2,
    } = {}
) {
    /**
     * Remove ações inválidas e ações ocultas.
     *
     * show === false
     *      => a ação não será exibida em nenhum dispositivo.
     */
    const visibleSystemActions = Array.isArray(actions) ? actions.filter((action) => action && (action.show ?? true)) : [];

    /**
     * Desktop.
     *
     * Todas as ações permanecem visíveis.
     */
    if (!isMobile) {
        return {
            visibleActions: visibleSystemActions,
            menuActions: [],
        };
    }

    /**
     * Procura ações marcadas para permanecerem visíveis no Mobile.
     *
     * Exemplo:
     *
     * {
     *      key: 'novo',
     *      mobileVisible: true
     * }
     */
    const mobileVisibleActions = visibleSystemActions
        .filter((action) => action.mobileVisible === true)
        .slice(0, maxMobileVisibleActions);

    /**
     * Encontrou ações configuradas para permanecerem visíveis?
     */
    if (mobileVisibleActions.length > 0) {
        /**
         * Cria um conjunto contendo apenas as keys das ações visíveis.
         * O Set torna a pesquisa muito rápida.
         */
        const visibleKeys = new Set(mobileVisibleActions.map((action) => action.key));

        return {
            /**
             * Botões que ficarão fora do menu.
             */
            visibleActions: mobileVisibleActions,

            /**
             * Todas as demais ações irão para o menu de opções.
             */
            menuActions: visibleSystemActions.filter((action) => !visibleKeys.has(action.key)),
        };
    }

    /**
     * Compatibilidade.
     *
     * Caso nenhuma ação tenha sido configurada com
     * mobileVisible=true,
     * mantém as primeiras ações visíveis.
     *
     * Isso permite migrar as telas gradativamente.
     */
    const visibleCount = Math.max(0, Number(defaultMobileVisibleCount) || 0);

    return {
        visibleActions: visibleSystemActions.slice(0, visibleCount),
        menuActions: visibleSystemActions.slice(visibleCount),
    };
}
